import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CreditCard } from "lucide-react";
import { toast } from "sonner";

const LOGIN_DEFAULTS = {
  app_name: "Vault",
  app_tagline: "Operational Coordination",
  logo_url: "/vault_logo.png",
  primary_color: "#D97706",
  sidebar_bg: "#1C1917",
};

export default function LoginPage({ onLogin }) {
  const [badgeInput, setBadgeInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(true);
  const [appSettings, setAppSettings] = useState(LOGIN_DEFAULTS);

  useEffect(() => {
    axios.get(`${API}/admin/app-settings`).then((r) => setAppSettings({ ...LOGIN_DEFAULTS, ...r.data })).catch(() => {});
  }, []);

  const handleBadgeScan = useCallback(async () => {
    const empNum = badgeInput.replace(/\D/g, "");
    if (!empNum) return;

    setLoading(true);
    setListening(false);

    try {
      const response = await axios.post(`${API}/auth/badge-scan`, {
        employee_number: empNum,
      });
      if (response.data?.user) {
        onLogin(response.data.user);
        toast.success(`Welcome, ${response.data.user.name}!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Badge not recognized");
      setBadgeInput("");
      setListening(true);
      setTimeout(() => document.getElementById("badge-input")?.focus(), 100);
    } finally {
      setLoading(false);
    }
  }, [badgeInput, onLogin]);

  // Auto-focus on mount
  useEffect(() => {
    document.getElementById("badge-input")?.focus();
  }, []);

  // Auto-submit on Enter (badge readers send Enter after scan)
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Enter" && badgeInput && listening) {
        handleBadgeScan();
      }
    };
    document.addEventListener("keypress", handleKeyPress);
    return () => document.removeEventListener("keypress", handleKeyPress);
  }, [badgeInput, listening, handleBadgeScan]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: appSettings.sidebar_bg }}>
      <div className="w-full max-w-md">
        <div className="bg-white border border-[#E7E5E4] rounded-sm p-8">
          <div className="flex justify-center mb-4">
            {appSettings.logo_url && <img src={appSettings.logo_url} alt={appSettings.app_name} className="h-20" data-testid="login-logo" />}
          </div>
          <h1 className="font-heading font-black text-2xl text-[#1C1917] text-center mb-1" data-testid="login-title">
            {appSettings.app_name}
          </h1>
          <p className="text-[#78716C] mb-6 text-sm text-center">
            Scan your employee badge to login
          </p>

          <div className={`text-center mb-6`}>
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-mono ${
              listening
                ? "bg-[#10B981] bg-opacity-10 text-[#10B981] border border-[#10B981] border-opacity-30"
                : "bg-[#64748B] bg-opacity-10 text-[#64748B] border border-[#64748B] border-opacity-30"
            }`}>
              <CreditCard size={16} />
              {listening ? "READY — SCAN BADGE" : "PROCESSING..."}
            </div>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleBadgeScan(); }} data-testid="badge-scan-form">
            <div className="mb-6">
              <Label htmlFor="badge-input" className="text-[#0F172A] font-medium sr-only">
                Badge Input
              </Label>
              <Input
                id="badge-input"
                type="text"
                inputMode="numeric"
                value={badgeInput}
                onChange={(e) => setBadgeInput(e.target.value.replace(/\D/g, ""))}
                placeholder="Waiting for badge scan..."
                autoFocus
                autoComplete="off"
                className="mt-1 rounded-none border-[#E2E8F0] focus:border-[#2563EB] focus:ring-[#2563EB] font-mono text-lg tracking-wider text-center"
                data-testid="badge-input"
                disabled={loading}
              />
            </div>

            <Button
              type="submit"
              disabled={loading || !badgeInput}
              className="w-full text-white rounded-none transition-colors duration-150 tracking-wide"
              style={{ backgroundColor: appSettings.primary_color }}
              data-testid="auth-submit-button"
            >
              <CreditCard size={16} className="mr-2" />
              {loading ? "Authenticating..." : "LOGIN"}
            </Button>
          </form>

          <div className="mt-6 bg-[#FAFAFA] border border-[#E2E8F0] p-4 rounded-sm">
            <p className="text-xs text-[#64748B]">
              <strong className="text-[#0F172A]">Instructions:</strong><br/>
              1. Click the input field above<br/>
              2. Scan your employee badge with the USB reader<br/>
              3. Login is automatic after scan
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
